import { useState, useEffect } from "react";
import { Gift, ShoppingCart, Check } from "lucide-react";
import { fetchProductByHandle } from "@/lib/shopify";
import type { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/store/cartStore";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { alertToast } from "@/contexts/AlertToastContext";
import { SEO } from "@/components/SEO";

const GIFT_CARD_HANDLE = "גיפט-כארד-תכשיטי-אליס";

const GiftCard = () => {
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    const loadGiftCard = async () => {
      setIsLoading(true);
      try {
        const giftCard = await fetchProductByHandle(GIFT_CARD_HANDLE);
        setProduct(giftCard);
        if (giftCard?.variants?.edges?.length) {
          setSelectedVariant(giftCard.variants.edges[0].node.id);
        }
      } catch (error) {
        console.error("Error loading gift card:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGiftCard();
  }, []);

  const handleAddToCart = () => {
    if (!product || !selectedVariant) return;

    const variant = product.variants?.edges?.find(
      (v) => v.node.id === selectedVariant
    )?.node;

    if (!variant) return;

    // Get image from product
    const productImage = product.images?.edges?.[0]?.node;
    
    addItem({
      product: product as any,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions,
      variantImage: productImage ? {
        url: productImage.url,
        altText: productImage.altText || undefined,
      } : undefined,
    });

    alertToast.success("כרטיס המתנה נוסף לסל", `₪${parseFloat(variant.price.amount).toLocaleString()}`);
  };

  const selectedVariantData = product?.variants?.edges?.find(
    (v) => v.node.id === selectedVariant
  )?.node;

  const productImage = product?.images?.edges?.[0]?.node.url;

  return (
    <PageTransition>
      <SEO
        title="כרטיס מתנה | גיפט קארד"
        description="כרטיס מתנה של אליס תכשיטים - הענקו למי שאתם אוהבים את האפשרות לבחור תכשיט מושלם. כרטיסי מתנה בסכומים שונים, נשלח למייל מיד לאחר הרכישה."
        canonical="https://alice.jewelry/gift-card"
      />
      <div className="min-h-screen flex flex-col" dir="rtl">
        <Header />
        <main className="flex-1">
          {/* Hero Section - starts right below header */}
          <section className="bg-gradient-to-b from-primary/5 to-transparent pt-8 md:pt-12 pb-6 md:pb-10">
            <div className="container mx-auto px-6 md:px-24">
              <div className="text-center max-w-2xl mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Gift className="w-7 h-7 text-primary" />
                </div>
                <h1 className="text-2xl md:text-3xl font-semibold mb-3">
                  כרטיס מתנה
                </h1>
                <p className="text-muted-foreground text-sm md:text-base">
                  {product?.description ||
                    "כרטיס המתנה של אליס שיעניק למי שאתם אוהבים את האפשרות לבחור את התכשיט המושלם עבורם"}
                </p>
              </div>
            </div>
          </section>

          {/* Gift Card Content */}
          <section className="py-8 md:py-12">
            <div className="container mx-auto px-6 md:px-24">
              {isLoading ? (
                <div className="max-w-4xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                    <Skeleton className="aspect-square rounded-lg" />
                    <div className="space-y-4">
                      <Skeleton className="h-6 w-32" />
                      <div className="grid grid-cols-3 gap-2">
                        {[...Array(7)].map((_, i) => (
                          <Skeleton key={i} className="h-11 rounded-lg" />
                        ))}
                      </div>
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </div>
                </div>
              ) : product ? (
                <div className="max-w-4xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
                    {/* Product Image - Sticky */}
                    <div className="md:sticky md:top-36">
                      {productImage ? (
                        <img
                          src={productImage}
                          alt={product.title}
                          className="w-full rounded-lg"
                        />
                      ) : (
                        <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                          <Gift className="w-16 h-16 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>

                    {/* Selection */}
                    <div className="space-y-5">
                      <div>
                        <h2 className="text-base font-medium mb-1">
                          בחרו סכום
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          לאחר הרכישה, קוד כרטיס המתנה יישלח למייל שלכם
                        </p>
                      </div>

                      {/* Amount Grid - Smaller & Elegant */}
                      <div className="grid grid-cols-3 gap-2">
                        {product.variants?.edges?.map(({ node: variant }) => {
                          const isSelected = selectedVariant === variant.id;
                          const price = parseFloat(variant.price.amount);

                          return (
                            <button
                              key={variant.id}
                              onClick={() => setSelectedVariant(variant.id)}
                              className={`group relative py-3 px-2 rounded-lg transition-all duration-200 text-center ${
                                isSelected
                                  ? "bg-primary/10 shadow-sm"
                                  : "bg-muted/30 hover:bg-primary/5"
                              }`}
                              style={{
                                border: isSelected 
                                  ? '1.5px solid hsl(var(--primary))' 
                                  : '1px solid hsl(var(--border))'
                              }}
                            >
                              {isSelected && (
                                <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-primary-foreground" />
                                </div>
                              )}
                              
                              <span className={`text-sm font-semibold transition-colors duration-200 ${
                                isSelected ? "text-primary" : "text-foreground group-hover:text-primary"
                              }`}>
                                ₪{price.toLocaleString()}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Selected Price */}
                      {selectedVariantData && (
                        <div className="bg-muted/20 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-0.5">
                            סכום נבחר
                          </p>
                          <p className="text-lg font-semibold text-primary">
                            ₪{parseFloat(selectedVariantData.price.amount).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {/* Add to Cart Button */}
                      <Button
                        onClick={handleAddToCart}
                        size="default"
                        className="w-full h-11 text-sm gap-2"
                        disabled={!selectedVariant}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        הוסף לסל
                      </Button>

                      {/* Info */}
                      <div className="bg-muted/10 rounded-lg p-3 space-y-2 text-xs text-muted-foreground">
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <p>כרטיס המתנה יישלח למייל שלכם מיד לאחר הרכישה</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <p>ניתן לממש את כרטיס המתנה בתשלום באתר בלבד</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <p>כרטיס המתנה אינו מוגבל בזמן</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Gift className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    כרטיס המתנה אינו זמין כרגע
                  </p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default GiftCard;
